import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pin, Link, ChevronDown, MessageCircle, RefreshCw } from 'lucide-react';
import { auth } from '../../config/firebase';

interface PinterestBoard {
  id: string;
  name: string;
  description?: string;
}

interface PinterestPostSettingsCardProps {
  title: string;
  link: string;
  selectedBoardId: string;
  allowComments: boolean;
  availableBoards: PinterestBoard[];
  onTitleChange: (title: string) => void;
  onLinkChange: (link: string) => void;
  onBoardChange: (boardId: string) => void;
  onAllowCommentsChange: (allow: boolean) => void;
  onRefreshBoards?: () => Promise<void>;
}

export const PinterestPostSettingsCard: React.FC<PinterestPostSettingsCardProps> = ({
  title,
  link,
  selectedBoardId,
  allowComments = true,
  availableBoards = [],
  onTitleChange,
  onLinkChange,
  onBoardChange,
  onAllowCommentsChange,
  onRefreshBoards
}) => {
  const { t } = useTranslation();
  const [isBoardDropdownOpen, setIsBoardDropdownOpen] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const selectedBoard = availableBoards.find(board => board.id === selectedBoardId);

  const handleBoardSelect = (boardId: string) => {
    onBoardChange(boardId);
    setIsBoardDropdownOpen(false);
  };

  const handleRefreshBoards = async () => {
    if (!onRefreshBoards) return;

    setIsRefreshing(true);
    try {
      await onRefreshBoards();
    } catch (error) {
      console.error('Error refreshing Pinterest boards:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Pin className="w-5 h-5 text-red-600" />
        <h2 className="text-lg font-semibold text-gray-900">{t('pinterest.settings.title', 'Pinterest Settings')}</h2>
      </div>

      <div className="space-y-4">
        {/* Pin Title */}
        <div>
          <label htmlFor="pinterestTitle" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pinterest.settings.pinTitle', 'Pin Title')}
          </label>
          <input
            type="text"
            id="pinterestTitle"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={t('pinterest.settings.pinTitlePlaceholder', 'Enter pin title (leave empty to use post content)')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Link */}
        <div>
          <label htmlFor="pinterestLink" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pinterest.settings.link', 'Link')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Link className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="url"
              id="pinterestLink"
              value={link}
              onChange={(e) => onLinkChange(e.target.value)}
              placeholder={t('pinterest.settings.linkPlaceholder', 'https://your-website.com')}
              className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {t('pinterest.settings.linkDescription', 'Link where users will be directed when they click your pin')}
          </p>
        </div>

        {/* Board Selection */}
        <div>
          <label htmlFor="pinterestBoard" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pinterest.settings.board', 'Board')}
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsBoardDropdownOpen(!isBoardDropdownOpen)}
              className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Pin className="w-4 h-4 text-red-600 mr-2" />
                <div className="text-left">
                  <p className={`font-medium ${!selectedBoard ? 'text-gray-500' : 'text-gray-900'}`}>
                    {selectedBoard ? selectedBoard.name : t('pinterest.settings.selectBoard', 'Select a board')}
                  </p>
                  {selectedBoard?.description && (
                    <p className="text-sm text-gray-500">{selectedBoard.description}</p>
                  )}
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isBoardDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isBoardDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                {availableBoards.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">
                    {t('pinterest.settings.noBoards', 'No boards available')}
                  </div>
                ) : (
                  availableBoards.map((board) => (
                    <button
                      key={board.id}
                      type="button"
                      onClick={() => handleBoardSelect(board.id)}
                      className={`w-full flex items-start p-3 hover:bg-red-50 transition-colors text-left ${
                        selectedBoardId === board.id ? 'bg-red-50' : ''
                      }`}
                    >
                      <Pin className="w-4 h-4 text-red-600 mr-3 mt-0.5" />
                      <div className="flex-grow">
                        <p className="font-medium text-gray-900">{board.name}</p>
                        {board.description && (
                          <p className="text-sm text-gray-500">{board.description}</p>
                        )}
                      </div>
                    </button>
                  ))
                )}

                {/* Refresh Boards Button */}
                {onRefreshBoards && (
                  <div className="border-t border-gray-200 p-2">
                    <button
                      type="button"
                      onClick={handleRefreshBoards}
                      disabled={isRefreshing}
                      className="w-full flex items-center justify-center gap-2 p-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                      <span>{isRefreshing ? t('pinterest.settings.refreshing', 'Refreshing...') : t('pinterest.settings.refreshBoards', 'Refresh boards')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {!selectedBoard && (
            <div className="mt-2 text-sm text-gray-600 flex items-center gap-1">
              <span>{t('pinterest.settings.boardRequired', 'Please select a board for your pin')}</span>
            </div>
          )}
        </div>

        {/* Allow Comments Toggle */}
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-gray-600" />
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  {t('pinterest.settings.allowComments', 'Allow Comments')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('pinterest.settings.allowCommentsDescription', 'Let people comment on your pin')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onAllowCommentsChange(!allowComments)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                allowComments ? 'bg-red-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  allowComments ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
